import React, { useState, useEffect, useRef } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItem,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import featureIncluded from "assets/icons/feature-included.svg";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAppSelector } from "redux/hooks";
import history from "utils/history";
import {
  handleErrorMessage,
  isTruthy,
  isOfferActive,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import {
  updatePlan,
  setDefaultCard,
  addPaymentMethod,
  addPaidCustomer,
  verifyCoupon,
} from "screens/Settings/MyPlan/PlanService";
import strings from "global/constants/StringConstants";
import Confetti from "global/components/Confetti";
import { activateUser } from "Services/CommonServices";
import urls from "global/constants/UrlConstants";

const cardStyle = {
  style: {
    base: {
      color: "black",
      fontFamily: "'Montserrat', sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "black",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const OnboardingStep3 = (props: {
  setStep?: Function;
  selectedPlan: any;
  setLoading: Function;
  cardData: any;
  getUserData?: Function;
  setUpgradePlanScreen?: Function;
  forOnboarding?: boolean;
  user?: any;
  loading?: boolean;
  isPlanAnnual: boolean;
  /**
   * Seats being bought. The subscription is created at this quantity, so it
   * travels with the purchase; a fresh sign-up starts with the one account
   * owner.
   */
  seats?: number;
}) => {
  const theme = useTheme();

  const { email, userName, account, appName, accessToken } = useAppSelector(
    (state) => state.auth,
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCardExpired = (expirationDate: string) => {
    if (!expirationDate) return false;
    const expiry = new Date(expirationDate);
    expiry.setHours(23, 59, 59, 999);
    const today = new Date();
    return today > expiry;
  };
  const [cardName, setCardName] = useState<string>(userName);
  const [isCardComplete, setIsCardComplete] = useState<boolean>(false);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [authorizeCharge, setAuthorizeCharge] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>(
    props.cardData.length > 0 ? "existing" : "new",
  );
  const [selectedCard, setSelectedCard] = useState<string>(() => {
    if (props.cardData && props.cardData.length > 0) {
      const defaultCard = props.cardData.find((card: any) => card.default);
      if (defaultCard && !isCardExpired(defaultCard.expirationDate)) {
        return defaultCard.id;
      }
      const firstValidCard = props.cardData.find(
        (card: any) => !isCardExpired(card.expirationDate),
      );
      return firstValidCard ? firstValidCard.id : "";
    }
    return "";
  });
  const [isDefaultCardSet, setIsDefaultCardSet] = useState<boolean>(false);
  const [previousSelectedCard, setPreviousSelectedCard] = useState<string>("");
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [payNowLoading, setPayNowLoading] = useState<boolean>(false);
  const [localCardData, setLocalCardData] = useState<any[]>([]);
  const [isCouponApplied, setIsCouponApplied] = useState<boolean>(false);
  const [discountedPrice, setDiscountedPrice] = useState<number>(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectedCardRef = useRef<HTMLLIElement | null>(null);

  const stripe = useStripe();
  const elements = useElements();
  const monthlyBase = props.selectedPlan.price;

  const currentTotalPrice = (() => {
    if (props.isPlanAnnual) {
      if (isCouponApplied && discountedPrice > 0) {
        return discountedPrice;
      }
      return props.selectedPlan.discountPrice * 12 || monthlyBase * 12;
    }
    return monthlyBase;
  })();

  useEffect(() => {
    const checkAnnualDiscount = async () => {
      if (props.isPlanAnnual) {
        if (isOfferActive())
          try {
            const couponCode = strings.OFFER_COUPON_CODE;
            setButtonLoading(true);
            const res = await verifyCoupon(
              couponCode,
              props.selectedPlan.id,
              account,
            );
            setDiscountedPrice(res);
            setIsCouponApplied(true);
            setButtonLoading(false);
          } catch (error) {
            console.error("Discount fetch Error:", error);
            setButtonLoading(false);
            setIsCouponApplied(false);
          }
      } else {
        setIsCouponApplied(false);
        setDiscountedPrice(0);
      }
    };
    checkAnnualDiscount();
  }, [props.isPlanAnnual, props.selectedPlan.id]);

  useEffect(() => {
    const updateCard = props.cardData.map((card: any) => ({
      ...card,
      isExpired: isCardExpired(card.expirationDate),
    }));

    setLocalCardData(updateCard);
    const defaultCard = updateCard.find((card: any) => card.default);
    if (
      defaultCard &&
      !defaultCard.isExpired &&
      defaultCard.id !== selectedCard
    ) {
      setSelectedCard(defaultCard.id);
      setPreviousSelectedCard(defaultCard.id);
    }
  }, [props.cardData, props.isPlanAnnual]);

  const setDefaultCardHandler = async () => {
    const selectedCardObj = localCardData.find(
      (card: any) => card.id === selectedCard,
    );

    if (selectedCardObj?.default) {
      if (previousSelectedCard === selectedCard) {
        openSuccessNotification("This card is already set as default");
        return;
      }
      setIsDefaultCardSet(true);
      setPreviousSelectedCard(selectedCard);
      return;
    }

    try {
      setButtonLoading(true);

      const updatedCards = localCardData.map((card: any) => ({
        ...card,
        default: card.id === selectedCard,
      }));
      setLocalCardData(updatedCards);

      const res = await setDefaultCard(selectedCard);

      setIsDefaultCardSet(true);
      setPreviousSelectedCard(selectedCard);

      if (props.getUserData) {
        await props.getUserData();
      }
      setButtonLoading(false);
      openSuccessNotification(
        res?.message || "Card set as default successfully",
      );
    } catch (error: any) {
      setLocalCardData(props.cardData);
      setIsDefaultCardSet(false);
      setPreviousSelectedCard(previousSelectedCard);
      setButtonLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleMakePayment = async () => {
    try {
      if (stripe !== null && elements !== null) {
        setButtonLoading(true);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: cardName,
            email: props.user ? props.user.email : email,
          },
        });
        if (error) {
          openErrorNotification(
            isTruthy(error) ? error.message : notifiers.GENERIC_ERROR,
          );
          setButtonLoading(false);
          return;
        } else {
          finalSubmitPayment(paymentMethod.id);
        }
      }
    } catch (err: any) {
      props.setStep && props.setStep(2);
      handleErrorMessage(err);
    }
  };

  const finalSubmitPayment = async (paymentId: any) => {
    const discountFlag = props.isPlanAnnual ? true : isCouponApplied;
    const data = {
      ...props.user,
      account: account,
      name: userName,
      contactNo: props.user ? props.user.contactNo : "",
      planId: props.selectedPlan.id,
      paymentMethodId: paymentId,
      email: props.forOnboarding ? props.user.email : email,
      // Buy-upfront: the subscription is created at this seat count.
      seats: Math.max(1, Number(props.seats) || 1),
    };
    try {
      props.setLoading(true);
      if (props.forOnboarding) {
        await activateUser(
          data,
          isTruthy(accessToken) ? accessToken : props.user?.authToken,
          discountFlag,
        );
        history.push(urls.landingViewPath);
        openSuccessNotification("Account Activated Successfully!");
      } else {
        const res = await addPaidCustomer(data, discountFlag);
        openSuccessNotification(
          "Success! Your plan has been updated. Changes will be applied to your account within 24 hours.",
        );
        props.getUserData && props.getUserData();
        props.setUpgradePlanScreen && props.setUpgradePlanScreen(false);
      }
      props.setLoading(false);
    } catch (error: any) {
      props.setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleSelectPaymentMethod = async () => {
    try {
      setButtonLoading(true);
      if (stripe !== null && elements !== null) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: cardName,
            email: props.user ? props.user.email : email,
          },
        });
        if (error) {
          openErrorNotification(
            isTruthy(error) ? error.message : notifiers.GENERIC_ERROR,
          );
          return;
        } else {
          if (localCardData.length > 0) {
            await addCardHandler(paymentMethod.id);
          }
        }
        setButtonLoading(false);
        setIsDefaultCardSet(true);
      }
    } catch (err: any) {
      setButtonLoading(false);
      props.setStep && props.setStep(2);
      handleErrorMessage(err);
    }
  };

  const addCardHandler = async (paymentMethodId: any) => {
    try {
      setButtonLoading(true);
      await addPaymentMethod(paymentMethodId, true);
      openSuccessNotification("Payment method added successfully");

      if (props.getUserData) {
        await props.getUserData();
      }

      const newDefaultCard = props.cardData.find((card: any) => card.default);
      if (newDefaultCard) {
        setSelectedCard(newDefaultCard.id);
        setPreviousSelectedCard(newDefaultCard.id);
      }

      setPaymentMethod("existing");
      setIsCardComplete(false);
      setIsDefaultCardSet(true);
      setButtonLoading(false);
    } catch (error: any) {
      setButtonLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleUpdatePlan = async () => {
    try {
      setPayNowLoading(true);
      const discountFlag = props.isPlanAnnual ? true : isCouponApplied;
      const res = await updatePlan(props.selectedPlan.id, discountFlag);
      openSuccessNotification(
        "Success! Your plan has been updated. Changes will be applied to your account within 24 hours.",
      );
      props.getUserData && props.getUserData();
      props.setUpgradePlanScreen && props.setUpgradePlanScreen(false);
      setPayNowLoading(false);
    } catch (error: any) {
      setPayNowLoading(false);
      handleErrorMessage(error);
    }
  };

  const getPaymentInformation = () => {
    return (
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <Stack bgcolor="#47556908" borderRadius="10px">
            <List
              sx={{
                listStyleType: "disc",
                padding: "10px 10px 10px 30px",
              }}
            >
              <ListItem
                sx={{
                  p: 0.5,
                  display: "list-item",
                  "::marker": {
                    color: "#475569",
                  },
                }}
              >
                <Typography variant="body2" color="text.primary">
                  We cannot refund once you purchased a subscription, but you
                  can always cancel
                </Typography>
              </ListItem>
              <ListItem
                sx={{
                  p: 0.5,
                  display: "list-item",
                  "::marker": {
                    color: "#475569",
                  },
                }}
              >
                <Typography variant="body2" color="text.primary">
                  <span style={{ fontWeight: 700 }}>Auto-renewal terms:</span>{" "}
                  Your subscription will automatically renew each month unless
                  you cancel. Your monthly fee may increase depending on your
                  contact tier level and email sends. You may call customer
                  support, prior to your renewal, to cancel your account. By
                  submitting your payment you acknowledge that you have read and
                  agree to our auto-renewal terms, the Terms of Service,
                  cancellation policy, and acknowledge receipt of our privacy
                  notice.
                </Typography>
              </ListItem>
            </List>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {(buttonLoading || payNowLoading) && (
        <Box
          sx={{
            // `inset: 0` rather than `100vw/100vh` — viewport units include
            // the scrollbar gutter.
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(255, 255, 255, 0.58)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={"1.5rem"} />
        </Box>
      )}
      <Stack
        direction="column"
        spacing={1}
        alignItems="start"
        p={isMobile ? 2 : 2}
      >
        {!props.forOnboarding ? (
          <Button
            id={`onboarding-step2-back-btn`}
            sx={{
              textDecoration: "none",
              textTransform: "none",
              color: "primary.main",
            }}
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => props.setStep && props.setStep(0)}
          >
            Back
          </Button>
        ) : (
          <Box width={"5%"}></Box>
        )}

        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          mt={3}
        >
          <Box
            sx={{
              width: isMobile ? "100%" : "95%",
              maxWidth: "1200px",
              bgcolor: "white",
              borderRadius: "10px",
              boxShadow: "6px 6px 18px rgba(171, 162, 173, 0.3)",
              p: isMobile ? 3 : 7,
            }}
          >
            <Grid container spacing={isMobile ? 4 : 8}>
              <Grid item xs={12} md={6}>
                <Box mb={4}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    mb={2}
                    color="text.primary"
                  >
                    Billing Information
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    {cardName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {isTruthy(email) ? email : props.user?.email}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    mb={1}
                    color="text.primary"
                  >
                    Payment Methods
                  </Typography>

                  <RadioGroup
                    aria-label="Payment method"
                    value={paymentMethod}
                    onChange={(e) => {
                      setPaymentMethod(e.target.value);
                    }}
                  >
                    <Stack direction="row" mb={2}>
                      <FormControlLabel
                        value="existing"
                        control={<Radio size="small" />}
                        label={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={
                              paymentMethod === "existing" ? 700 : 400
                            }
                          >
                            Use Existing Cards
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="new"
                        control={<Radio size="small" />}
                        label={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={paymentMethod === "new" ? 700 : 400}
                          >
                            Add New Card
                          </Typography>
                        }
                      />
                    </Stack>
                  </RadioGroup>

                  {paymentMethod === "existing" ? (
                    <Box
                      sx={{
                        position: "relative",
                        border: "1px solid #E0E0E0",
                        borderRadius: "10px",
                        overflow: "hidden",
                        mt: 1,
                        minHeight: "100px",
                      }}
                    >
                      {localCardData.length > 0 ? (
                        <Paper
                          ref={listRef}
                          elevation={0}
                          sx={{
                            maxHeight: {
                              xl: 420,
                              lg: 400,
                              md: 360,
                              sm: 320,
                              xs: 280,
                            },
                            overflowY: "auto",
                            overflowX: "hidden",
                            pr: 1,
                            boxShadow: "none",
                            scrollbarWidth: "thin",
                          }}
                        >
                          <List
                            disablePadding
                            sx={{ opacity: buttonLoading ? 0.5 : 1 }}
                          >
                            {localCardData.map((card: any, index: number) => (
                              <ListItem
                                key={card.id}
                                ref={
                                  card.id === selectedCard
                                    ? selectedCardRef
                                    : undefined
                                }
                                onClick={() => {
                                  if (buttonLoading || card.isExpired) return;
                                  setSelectedCard(card.id);
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  py: 1.25,
                                  px: 1.5,
                                  cursor: buttonLoading
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                disableGutters
                                divider={false}
                              >
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                >
                                  <Radio
                                    size="small"
                                    checked={selectedCard === card.id}
                                    onChange={() => setSelectedCard(card.id)}
                                    disabled={buttonLoading || card.isExpired}
                                    inputProps={{
                                      "aria-label": `Card ending ${card.number}${
                                        card.isExpired ? " (expired)" : ""
                                      }`,
                                    }}
                                    sx={{
                                      p: 0,
                                      color: "primary.main",
                                      "&.Mui-checked": { color: "primary.main" },
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight:
                                        selectedCard === card.id ? 700 : 400,
                                      color: card.isExpired
                                        ? "#9CA3AF"
                                        : "#64748B",
                                      fontSize: "14px",
                                      letterSpacing: "1px",
                                    }}
                                  >
                                    XXXX-XXXX-XXXX-{card.number}
                                  </Typography>
                                </Stack>
                                {card.default && (
                                  <Chip
                                    label="Active"
                                    size="small"
                                    sx={{
                                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                      ml: "12px",
                                      color: "primary.main",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      borderRadius: "16px",
                                      height: "24px",
                                    }}
                                  />
                                )}
                                {card.isExpired && (
                                  <Chip
                                    label="Expired"
                                    size="small"
                                    sx={{
                                      bgcolor: "#FEE2E2",
                                      ml: "12px",
                                      color: "#991B1B",
                                      fontSize: "12px",
                                      borderRadius: "16px",
                                      height: 22,
                                    }}
                                  />
                                )}
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "100px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "#64748B", p: 1 }}
                          >
                            No cards available, please add a new card.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ position: "relative", mt: 1 }}>
                      <Box
                        sx={{
                          border: "#47556940 solid 1px",
                          borderRadius: "9px",
                          padding: "12px",
                          backgroundColor: "#fff",
                          opacity: buttonLoading ? 0.5 : 1,
                        }}
                      >
                        <CardElement
                          options={cardStyle}
                          id="card_number"
                          onChange={(e) => setIsCardComplete(e.complete)}
                        />
                      </Box>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 3,
                      bgcolor: "primary.main",
                      "&.Mui-disabled": {
                        bgcolor: "action.disabledBackground",
                      },
                      textTransform: "none",
                      py: 1.5,
                    }}
                    disabled={
                      buttonLoading ||
                      (paymentMethod === "new" && !isCardComplete) ||
                      (paymentMethod === "existing" &&
                        (!localCardData ||
                          localCardData.length === 0 ||
                          localCardData.find((c: any) => c.id === selectedCard)
                            ?.isExpired))
                    }
                    onClick={() => {
                      if (paymentMethod === "existing") {
                        setDefaultCardHandler();
                      } else {
                        handleSelectPaymentMethod();
                      }
                    }}
                  >
                    {paymentMethod === "existing"
                      ? "Confirm Payment Method"
                      : "Add New Card"}
                  </Button>

                  <Box
                    sx={{
                      mt: 3,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      p: 2,
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Your billing details are fully encrypted and securely
                      handled by our trusted payment partner
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    opacity: isDefaultCardSet ? 1 : 0.5,
                    pointerEvents: isDefaultCardSet ? "auto" : "none",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                    <Typography
                      component="p"
                      variant="h1"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ${props.isPlanAnnual ? currentTotalPrice : monthlyBase}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {props.isPlanAnnual ? "every year" : "every month"}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography variant="h6" fontWeight={700} color={"primary.main"}>
                      {props.selectedPlan.name} plan
                    </Typography>
                    <Box display={"flex"} alignItems="baseline" gap={0.5}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color={"primary.main"}
                      >
                        $
                        {props.isPlanAnnual
                          ? props.selectedPlan.discountPrice
                          : monthlyBase.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        / per month
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    spacing={1.5}
                    mb={4}
                    sx={{
                      maxHeight: {
                        xl: 220,
                        lg: 200,
                        md: 180,
                        sm: 160,
                        xs: 140,
                      },
                      overflowX: "hidden",
                      overflowY: "auto",
                      border: "1px solid #E0E0E0",
                      borderRadius: "10px",
                      px: 1.5,
                      py: 1.25,
                      pr: 1,
                      scrollbarWidth: "thin",
                    }}
                  >
                    {props.selectedPlan.feature.map(
                      (feature: string, idx: number) => (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{
                            minHeight: 28,
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              bgcolor: "text.primary",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={featureIncluded}
                              alt="included"
                              style={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#fff",
                              }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.primary">
                            {feature}
                          </Typography>
                        </Stack>
                      ),
                    )}
                  </Stack>

                  <Stack spacing={2} mb={4}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      color="primary.main"
                    >
                      <Typography variant="body1" fontWeight="600">
                        Subtotal
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {props.isPlanAnnual
                          ? (monthlyBase * 12).toFixed(2)
                          : monthlyBase.toFixed(2)}
                      </Typography>
                    </Stack>
                    {props.isPlanAnnual &&
                      monthlyBase * 12 > currentTotalPrice && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          color="success.main"
                        >
                          <Typography variant="body1" fontWeight="600">
                            Discount (Annual Saving)
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            -$
                            {(monthlyBase * 12 - currentTotalPrice).toFixed(2)}
                          </Typography>
                        </Stack>
                      )}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      color="primary.main"
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Total amount due
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        ${currentTotalPrice.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack spacing={2} mb={3}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mt: 0.2 }}
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        disabled={!isDefaultCardSet}
                        inputProps={{
                          "aria-label":
                            "I agree to the terms of use and privacy policy of Inkfree.",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        color="text.primary"
                        fontWeight={500}
                      >
                        I agree to the terms of use and privacy policy of
                        Inkfree.
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mt: 0.2 }}
                        checked={authorizeCharge}
                        onChange={(e) => setAuthorizeCharge(e.target.checked)}
                        disabled={!isDefaultCardSet}
                        inputProps={{
                          "aria-label": `I authorize Inkfree to automatically charge me ${
                            props.isPlanAnnual ? "every year" : "every month"
                          } based on the subscription.`,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        color="text.primary"
                        fontWeight={500}
                      >
                        I authorize Inkfree to automatically charge me{" "}
                        {props.isPlanAnnual ? "every year" : "every month"}{" "}
                        based on the subscription.
                      </Typography>
                    </Stack>

                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 2,
                        bgcolor: "primary.main",
                        "&.Mui-disabled": {
                          bgcolor: "action.disabledBackground",
                        },
                        textTransform: "none",
                        py: 1.5,
                      }}
                      disabled={
                        !agreeTerms ||
                        !authorizeCharge ||
                        !isDefaultCardSet ||
                        payNowLoading
                      }
                      onClick={() =>
                        props.cardData.length > 0
                          ? handleUpdatePlan()
                          : handleMakePayment()
                      }
                    >
                      Pay Now
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {getPaymentInformation()}
        </Stack>
      </Stack>
      <Confetti
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
    </>
  );
};

export default OnboardingStep3;
